import { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  TextField,
  Chip,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Avatar,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import {
  fetchChecklistEvaluation,
  saveEvaluationDraft,
  submitInstructorSignOff,
} from "../../api/backend";

import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import SaveIcon from "@mui/icons-material/Save";
import HowToRegIcon from "@mui/icons-material/HowToReg";

const DS = {
  surface: "var(--color-surface-2)",
  border: "rgba(0,0,0,0.08)",
};

function ResultToggle({ value, onChange }) {
  return (
    <ToggleButtonGroup
      exclusive
      value={value}
      onChange={(_, v) => v && onChange(v)}
      size="small"
    >
      <ToggleButton
        value="PASS"
        sx={{
          px: 1.5, fontSize: "0.72rem", fontWeight: 700,
          "&.Mui-selected": { bgcolor: "var(--color-success-bg)", color: "#15803d", borderColor: "var(--color-success-bg)" },
        }}
      >
        <CheckCircleIcon sx={{ fontSize: 14, mr: 0.5 }} /> Pass
      </ToggleButton>
      <ToggleButton
        value="FAIL"
        sx={{
          px: 1.5, fontSize: "0.72rem", fontWeight: 700,
          "&.Mui-selected": { bgcolor: "var(--color-danger-bg)", color: "var(--color-danger)", borderColor: "var(--color-danger-bg)" },
        }}
      >
        <CancelIcon sx={{ fontSize: 14, mr: 0.5 }} /> Fail
      </ToggleButton>
      <ToggleButton
        value="N/A"
        sx={{
          px: 1.5, fontSize: "0.72rem", fontWeight: 700,
          "&.Mui-selected": { bgcolor: "var(--color-surface-2)", color: "var(--color-ink-2)", borderColor: "var(--color-hairline-cloud)" },
        }}
      >
        <RemoveCircleOutlineIcon sx={{ fontSize: 14, mr: 0.5 }} /> N/A
      </ToggleButton>
    </ToggleButtonGroup>
  );
}

export default function ChecklistSignOffPage() {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const { courseId, checklistId, studentId } = useParams();
  const navigate = useNavigate();

  const [evaluation, setEvaluation] = useState(null);
  const [itemResults, setItemResults] = useState({});
  const [finalResult, setFinalResult] = useState(null);
  const [explanation, setExplanation] = useState("");
  const [overridePeerPrereq, setOverridePeerPrereq] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!checklistId || !studentId) return;
    fetchChecklistEvaluation(courseId, checklistId, studentId)
      .then((data) => {
        setEvaluation(data);
        // Pre-populate item results from existing instructor results or peer results
        const initial = {};
        (data.items || []).forEach((item) => {
          const existing = data.instructorResults?.[item.id] || data.peerResults?.[item.id];
          if (existing) initial[item.id] = existing;
        });
        setItemResults(initial);
        if (data.instructorFinalResult) setFinalResult(data.instructorFinalResult);
        if (data.instructorExplanation) setExplanation(data.instructorExplanation);
      })
      .catch((err) => {
        setError("We couldn't load this right now. Please try again.");
        console.error(err);
      })
      .finally(() => setLoading(false));
  }, [courseId, checklistId, studentId]);

  const passCount = Object.values(itemResults).filter((v) => v === "PASS").length;
  const totalScorable = Object.values(itemResults).filter((v) => v !== "N/A").length;
  const gradePercent = totalScorable > 0 ? Math.round((passCount / totalScorable) * 100) : null;

  const handleItemChange = (itemId, val) => {
    setItemResults((prev) => ({ ...prev, [itemId]: val }));
  };

  const handleSaveDraft = () => {
    setSaving(true);
    const payload = { itemResults, finalResult, explanation, gradePercent };
    saveEvaluationDraft(courseId, checklistId, studentId, payload)
      .then(() => {})
      .catch((err) => console.error("Draft save failed:", err))
      .finally(() => setSaving(false));
  };

  const handleSubmit = () => {
    if (!finalResult) {
      setError("Select a final Pass or Fail determination before submitting.");
      return;
    }
    if (finalResult === "FAIL" && !explanation.trim()) {
      setError("A written explanation is required when the final result is Fail.");
      return;
    }
    setError(null);
    setSubmitting(true);
    const payload = {
      itemResults,
      finalResult,
      explanation,
      gradePercent,
      overridePeerPrereq,
    };
    submitInstructorSignOff(courseId, checklistId, studentId, payload)
      .then(() => setSuccess(true))
      .catch((err) => {
        setError("Sign-off submission failed. " + (err?.response?.data?.error || err?.response?.data?.message || ""));
        console.error(err);
      })
      .finally(() => setSubmitting(false));
  };

  const peerMetPrereq = evaluation?.peerPrereqMet ?? false;
  const requiresExplanation = finalResult === "FAIL";

  if (success) {
    return (
      <Box sx={{ maxWidth: 700, mx: "auto", textAlign: "center", pt: 10 }}>
        <CheckCircleIcon sx={{ fontSize: 64, color: "var(--color-success)", mb: 2 }} />
        <Typography variant="h5" fontWeight={800} color={tPrimary} gutterBottom sx={{ fontFamily: "var(--font-display)" }}>
          Sign-Off Submitted
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          The instructor sign-off has been recorded for this student.
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate(-1)}
          sx={{ bgcolor: tPrimary, borderRadius: "8px", textTransform: "none", fontWeight: 700 }}
        >
          Back to Course
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 860, mx: "auto" }}>

      {/* Breadcrumb */}
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2.5 }}>
        <Link
          underline="hover" color="inherit" sx={{ cursor: "pointer", fontSize: "0.85rem" }}
          onClick={() => navigate("/instructor/courses")}
        >
          Assigned Classes
        </Link>
        {courseId && (
          <Link
            underline="hover" color="inherit" sx={{ cursor: "pointer", fontSize: "0.85rem" }}
            onClick={() => navigate(`/course-management/${courseId}`)}
          >
            Course Hub
          </Link>
        )}
        <Typography color="text.primary" sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
          Checklist Sign-Off
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={800} color={tPrimary} sx={{ fontFamily: "var(--font-display)" }}>
          {evaluation?.checklistTitle || "Skill Checklist Sign-Off"}
        </Typography>
        {evaluation?.studentName && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.75 }}>
            <Avatar sx={{ width: 28, height: 28, bgcolor: tPrimary, fontSize: "0.75rem" }}>
              {evaluation.studentName[0]}
            </Avatar>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              {evaluation.studentName}
            </Typography>
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 2.5, borderRadius: "10px" }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Peer pre-req status */}
      {evaluation && (
        <Alert
          severity={peerMetPrereq ? "success" : "warning"}
          sx={{ mb: 2.5, borderRadius: "10px" }}
          action={
            !peerMetPrereq && (
              <Button
                size="small"
                color="inherit"
                sx={{ fontWeight: 700, fontSize: "0.75rem" }}
                onClick={() => setOverridePeerPrereq(true)}
              >
                {overridePeerPrereq ? "Override Active" : "Override"}
              </Button>
            )
          }
        >
          {peerMetPrereq
            ? "Peer pre-requisite met — eligible for instructor sign-off."
            : overridePeerPrereq
            ? "Peer pre-requisite not met — override active. You may sign off anyway."
            : "Peer pre-requisite not yet met. Sign-off requires peer completion or instructor override."}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Checklist items table */}
          <Paper
            elevation={0}
            sx={{ borderRadius: "12px", border: `1px solid ${DS.border}`, overflow: "hidden", mb: 3 }}
          >
            <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${DS.border}` }}>
              <Typography variant="subtitle1" fontWeight={700} color={tPrimary} sx={{ fontFamily: "var(--font-display)" }}>
                Performance Criteria
              </Typography>
              {gradePercent !== null && (
                <Typography variant="caption" color="text.secondary">
                  Grade: <strong>{gradePercent}%</strong> ({passCount} pass of {totalScorable} scorable items)
                </Typography>
              )}
            </Box>

            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: DS.surface }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem", color: "text.secondary", width: "50%" }}>CRITERIA</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem", color: "text.secondary" }}>PEER RESULT</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem", color: "text.secondary" }}>INSTRUCTOR</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(evaluation?.items || []).map((item) => {
                  const peerResult = evaluation?.peerResults?.[item.id];
                  return (
                    <TableRow key={item.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{item.label || item.description}</Typography>
                      </TableCell>
                      <TableCell>
                        {peerResult ? (
                          <Chip
                            label={peerResult}
                            size="small"
                            sx={{
                              bgcolor:
                                peerResult === "PASS" ? "var(--color-success-bg)" :
                                peerResult === "FAIL" ? "var(--color-danger-bg)" : "var(--color-surface-2)",
                              color:
                                peerResult === "PASS" ? "#15803d" :
                                peerResult === "FAIL" ? "var(--color-danger)" : "var(--color-ink-2)",
                              fontSize: "0.7rem", fontWeight: 700,
                            }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <ResultToggle
                          value={itemResults[item.id] || null}
                          onChange={(v) => handleItemChange(item.id, v)}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
                {(!evaluation?.items || evaluation.items.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No checklist items found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>

          {/* Final determination */}
          <Paper
            elevation={0}
            sx={{ borderRadius: "12px", border: `1px solid ${DS.border}`, p: 2.5, mb: 3 }}
          >
            <Typography variant="subtitle1" fontWeight={700} color={tPrimary} sx={{ mb: 2, fontFamily: "var(--font-display)" }}>
              Final Determination
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5 }}>
              <Typography variant="body2" fontWeight={600} sx={{ minWidth: 120 }}>
                Final Result
              </Typography>
              <ToggleButtonGroup
                exclusive
                value={finalResult}
                onChange={(_, v) => v && setFinalResult(v)}
              >
                <ToggleButton
                  value="PASS"
                  sx={{
                    px: 3, fontWeight: 700,
                    "&.Mui-selected": { bgcolor: "var(--color-success-bg)", color: "#15803d", borderColor: "var(--color-success-bg)" },
                  }}
                >
                  PASS
                </ToggleButton>
                <ToggleButton
                  value="FAIL"
                  sx={{
                    px: 3, fontWeight: 700,
                    "&.Mui-selected": { bgcolor: "var(--color-danger-bg)", color: "var(--color-danger)", borderColor: "var(--color-danger-bg)" },
                  }}
                >
                  FAIL
                </ToggleButton>
              </ToggleButtonGroup>
              {gradePercent !== null && (
                <Chip
                  label={`${gradePercent}% Grade`}
                  size="small"
                  sx={{
                    bgcolor: gradePercent >= 70 ? "var(--color-success-bg)" : "var(--color-danger-bg)",
                    color: gradePercent >= 70 ? "#15803d" : "var(--color-danger)",
                    fontWeight: 700, fontSize: "0.78rem",
                  }}
                />
              )}
            </Box>

            <TextField
              label={requiresExplanation ? "Written Explanation (required for Fail)" : "Written Explanation (optional)"}
              multiline
              rows={4}
              fullWidth
              value={explanation}
              onChange={(e) => setExplanation(e.target.value)}
              required={requiresExplanation}
              error={requiresExplanation && !explanation.trim()}
              helperText={
                requiresExplanation && !explanation.trim()
                  ? "An explanation is required when the grade is below 70% or when result is Fail."
                  : ""
              }
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            />
          </Paper>

          <Divider sx={{ mb: 2.5 }} />

          {/* Action buttons */}
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              startIcon={<SaveIcon />}
              onClick={handleSaveDraft}
              disabled={saving}
              sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 700, borderColor: tPrimary, color: tPrimary }}
            >
              {saving ? "Saving…" : "Save Draft"}
            </Button>
            <Button
              variant="contained"
              startIcon={<HowToRegIcon />}
              onClick={handleSubmit}
              disabled={submitting || (!peerMetPrereq && !overridePeerPrereq)}
              sx={{
                bgcolor: tPrimary,
                "&:hover": { bgcolor: "#002d56" },
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              {submitting ? "Submitting…" : "Submit Sign-Off"}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}
